import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { RegistrationPlate } from '@/components/common/RegistrationPlate';
import { formatCentsForDisplay } from '@/utils/money';
import { useAuthStore } from '@/stores/authStore';
import { useNotify } from '@/hooks/useNotify';
import { describeApiError } from '@/lib/describeApiError';
import {
  openCourtesyLoanPdf,
  useWorkOrderCourtesyLoans,
  type CourtesyLoan,
} from '@/hooks/useCourtesyVehicles';
import { CourtesyLoanStatusChip } from './CourtesyLoanStatusChip';
import { CourtesyReserveDialog, type CourtesyWorkOrderRef } from './CourtesyReserveDialog';
import {
  CourtesyLoanDialog,
  type CourtesyLoanDialogMode,
} from './CourtesyLoanDialog';

interface CourtesyVehicleCardProps {
  workOrder: CourtesyWorkOrderRef;
}

export function CourtesyVehicleCard({ workOrder }: CourtesyVehicleCardProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canUpdate = hasPermission('work_orders.update');

  const { data: loans, isError } = useWorkOrderCourtesyLoans(workOrder.id);
  const activeLoans = (loans ?? []).filter((loan) => loan.status !== 'cancelled');

  const [reserveOpen, setReserveOpen] = useState(false);
  const [dialogLoan, setDialogLoan] = useState<CourtesyLoan | null>(null);
  const [dialogMode, setDialogMode] = useState<CourtesyLoanDialogMode>('view');

  const openLoan = (loan: CourtesyLoan, mode: CourtesyLoanDialogMode) => {
    setDialogLoan(loan);
    setDialogMode(mode);
  };

  const handlePdf = async (loan: CourtesyLoan) => {
    try {
      await openCourtesyLoanPdf(loan.id);
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('courtesy.pdfFailed') }));
    }
  };

  if (isError) {
    return <Alert severity="error">{t('courtesy.loadFailed')}</Alert>;
  }

  return (
    <Box>
      <Stack spacing={2}>
        {activeLoans.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t('courtesy.empty')}
          </Typography>
        )}
        {activeLoans.map((loan) => (
          <Stack
            key={loan.id}
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <RegistrationPlate registrationNumber={loan.registrationNumber} />
              <CourtesyLoanStatusChip status={loan.status} />
              {loan.priceCents != null && loan.priceCents > 0 && (
                <Typography variant="body2">{formatCentsForDisplay(loan.priceCents)}</Typography>
              )}
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {canUpdate && loan.status === 'reserved' && (
                <Button size="small" onClick={() => { openLoan(loan, 'handover'); }}>
                  {t('courtesy.handover')}
                </Button>
              )}
              {canUpdate && loan.status === 'out' && (
                <Button size="small" onClick={() => { openLoan(loan, 'return'); }}>
                  {t('courtesy.return')}
                </Button>
              )}
              {canUpdate && loan.status === 'reserved' && (
                <Button
                  size="small"
                  color="error"
                  onClick={() => { openLoan(loan, 'view'); }}
                >
                  {t('courtesy.cancel')}
                </Button>
              )}
              {(loan.status === 'out' || loan.status === 'returned') && (
                <Tooltip title={t('courtesy.openPdf')}>
                  <IconButton
                    size="small"
                    onClick={() => { void handlePdf(loan); }}
                    aria-label={t('courtesy.openPdf')}
                    sx={{ minWidth: 44, minHeight: 44 }}
                  >
                    <PictureAsPdfIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Stack>
        ))}
        {canUpdate && (
          <Box>
            <Button variant="outlined" onClick={() => { setReserveOpen(true); }}>
              {t('courtesy.reserve')}
            </Button>
          </Box>
        )}
      </Stack>

      <CourtesyReserveDialog
        open={reserveOpen}
        workOrder={workOrder}
        onClose={() => { setReserveOpen(false); }}
      />
      <CourtesyLoanDialog
        open={dialogLoan != null}
        loan={dialogLoan}
        mode={dialogMode}
        canUpdate={canUpdate}
        onClose={() => { setDialogLoan(null); }}
        onRequestMode={(next) => { setDialogMode(next); }}
      />
    </Box>
  );
}
